/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import {
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    InputRightElement,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { faCalendar, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import { FaIcon } from '../fa-icon';

function DateInputRefFunction(
    {
        size = 'md',
        inputGroupProps = {},
        isPopoverOpen,
        selectedDate,
        onClear,
        displayFormat,
        noDateSelectedText,
        allowClear,
        placeholder: inputPlaceholder,
        ...inputProps
    }: {
        size: React.ComponentProps<typeof InputGroup>['size'];
        inputGroupProps?: Omit<React.ComponentProps<typeof InputGroup>, 'size'>;
        isPopoverOpen: boolean;
        selectedDate?: Date | null;
        displayFormat: string;
        noDateSelectedText: string;
        onClear: () => void;
        allowClear: boolean;
    } & Omit<React.ComponentProps<typeof Input>, 'ref'>,
    ref: React.ForwardedRef<HTMLInputElement>
) {
    const dateValue = useMemo<string>(() => {
        if (selectedDate === undefined || selectedDate === null) {
            return '';
        }

        return format(selectedDate, displayFormat);
    }, [selectedDate, displayFormat, noDateSelectedText]);

    const placeholder = useMemo<string | undefined>(() => {
        if (selectedDate === null && noDateSelectedText !== '') {
            return noDateSelectedText;
        }

        return inputPlaceholder;
    }, [inputPlaceholder, noDateSelectedText, selectedDate]);

    return (
        <InputGroup size={size} cursor="pointer" {...inputGroupProps}>
            <InputLeftElement color="gray.500" pointerEvents="none">
                <FaIcon icon={faCalendar} />
            </InputLeftElement>
            <Input
                ref={ref}
                isReadOnly
                _readOnly={{
                    userSelect: 'all',
                }}
                data-focus-visible={isPopoverOpen || undefined}
                cursor="pointer"
                value={dateValue}
                placeholder={placeholder}
                {...inputProps}
            />
            <InputRightElement as={HStack} pr={3} justifyContent="flex-end" w="20" color="black" pointerEvents="none" gap={0}>
                {selectedDate !== undefined && selectedDate !== null && allowClear && (
                    <IconButton
                        colorScheme="blackAlpha"
                        size="xs"
                        pointerEvents="all"
                        variant="ghost"
                        aria-label="clear selection"
                        icon={<FaIcon icon={faTimes} />}
                        onClick={onClear}
                    />
                )}
                <Icon fontSize={size}>
                    <path fill="currentColor" d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
                </Icon>
            </InputRightElement>
        </InputGroup>
    );
}

export const DateInput = React.forwardRef(DateInputRefFunction);
